import React, { useEffect, useRef, useState } from 'react';
import Input from './input';
import Label from './label';
import { cn } from '../../lib/utils';
import mapboxgl from 'mapbox-gl';
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';
import '../../styles/mapbox-geocoder.css';

const MapboxPlacesAutocomplete = ({
	id,
	name,
	label,
	value,
	onChange,
	placeholder,
	required,
	error,
	className,
	disabled,
}) => {
	const geocoderContainerRef = useRef(null);
	const geocoderRef = useRef(null);
	const [initializationFailed, setInitializationFailed] = useState(false);
	const [debugInfo, setDebugInfo] = useState('');

	useEffect(() => {
		// Only initialize if container exists and geocoder doesn't
		if (!geocoderContainerRef.current || geocoderRef.current) return;

		const accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;
		console.log('Mapbox token:', accessToken ? 'Present' : 'Missing');
		setDebugInfo(`Token status: ${accessToken ? 'Present' : 'Missing'}`);

		if (!accessToken) {
			console.error('Mapbox access token is not configured');
			setInitializationFailed(true);
			setDebugInfo('Error: Mapbox access token is not configured');
			return;
		}

		try {
			// Initialize mapboxgl
			mapboxgl.accessToken = accessToken;

			// Create a temporary map element (required for geocoder)
			const tempMap = new mapboxgl.Map({
				container: document.createElement('div'),
				style: 'mapbox://styles/mapbox/streets-v11',
			});

			console.log('Initializing Mapbox Geocoder...');
			const geocoder = new MapboxGeocoder({
				accessToken,
				types: 'address',
				countries: 'us',
				placeholder: placeholder || 'Enter an address',
				marker: false,
				mapboxgl: tempMap,
				flyTo: false,
				clearOnBlur: false,
				limit: 5,
				minLength: 1,
			});

			// Handle the result
			geocoder.on('result', (e) => {
				const { result } = e;
				if (!result) return;

				const address = {
					street: `${result.address || ''} ${result.text || ''}`.trim(),
					city:
						result.context?.find((c) => c.id.startsWith('place.'))?.text || '',
					state:
						result.context?.find((c) => c.id.startsWith('region.'))?.text || '',
					zip:
						result.context?.find((c) => c.id.startsWith('postcode.'))?.text ||
						'',
					coordinates: result.center || null,
					formatted_address: result.place_name || '',
				};

				onChange(address);
			});

			// Handle clear
			geocoder.on('clear', () => {
				onChange({
					street: '',
					city: '',
					state: '',
					zip: '',
					coordinates: null,
					formatted_address: '',
				});
			});

			// Add geocoder to container
			console.log('Adding geocoder to container...');
			geocoderContainerRef.current.innerHTML = ''; // Clear any existing content
			geocoder.addTo(geocoderContainerRef.current);
			geocoderRef.current = geocoder;

			// Set initial value if provided
			if (value && typeof value === 'object') {
				const displayValue = value.formatted_address || value.street;
				if (displayValue) {
					geocoder.setInput(displayValue);
				}
			}

			// Add attributes to input and ensure it's interactive
			const inputEl = geocoderContainerRef.current.querySelector('input');
			if (inputEl) {
				inputEl.id = id;
				inputEl.name = name;
				inputEl.required = required;
				inputEl.disabled = disabled;
				inputEl.setAttribute('aria-label', label || 'Address search');
				inputEl.style.pointerEvents = 'auto';
				inputEl.style.cursor = 'text';

				// Force the input to be interactive
				inputEl.addEventListener('click', (e) => e.stopPropagation());
				inputEl.addEventListener('focus', (e) => e.stopPropagation());
			}

			setDebugInfo('Mapbox initialization successful');

			// Cleanup function
			return () => {
				if (geocoderRef.current) {
					geocoderRef.current.onRemove();
					geocoderRef.current = null;
				}
				tempMap.remove();
			};
		} catch (error) {
			console.error('Error initializing Mapbox Geocoder:', error);
			setInitializationFailed(true);
			setDebugInfo(`Error initializing Mapbox: ${error.message}`);
		}
	}, [id, name, label, placeholder, required, disabled]); // Dependencies that affect initialization

	// Update the input value when the value prop changes
	useEffect(() => {
		if (geocoderRef.current && value && typeof value === 'object') {
			const displayValue = value.formatted_address || value.street;
			if (displayValue) {
				geocoderRef.current.setInput(displayValue);
			}
		}
	}, [value]);

	// If initialization failed, render a regular input as fallback
	if (initializationFailed) {
		return (
			<div className={cn('space-y-2', className)}>
				{label && (
					<Label htmlFor={id} className={cn(required && 'required')}>
						{label}
					</Label>
				)}
				<Input
					id={id}
					name={name}
					value={value?.formatted_address || value?.street || ''}
					onChange={(e) => {
						onChange({
							street: e.target.value,
							city: '',
							state: '',
							zip: '',
							coordinates: null,
							formatted_address: e.target.value,
						});
					}}
					placeholder={placeholder || 'Enter an address'}
					required={required}
					disabled={disabled}
					className={cn(error && 'border-red-500')}
				/>
				{error && <p className='text-sm text-red-500'>{error}</p>}
				<p className='text-xs text-yellow-600'>{debugInfo}</p>
			</div>
		);
	}

	return (
		<div className={cn('space-y-2 w-full', className)}>
			{label && (
				<Label htmlFor={id} className={cn(required && 'required')}>
					{label}
				</Label>
			)}
			<div className='relative w-full'>
				<div
					ref={geocoderContainerRef}
					className={cn(
						'mapboxgl-ctrl-geocoder w-full',
						disabled && 'pointer-events-none opacity-50',
						error && 'error'
					)}
					style={{ width: '100%', minWidth: '100%' }}
				/>
			</div>
			{error && <p className='text-sm text-red-500'>{error}</p>}
			<p className='text-xs text-yellow-600'>{debugInfo}</p>
		</div>
	);
};

export default MapboxPlacesAutocomplete;
